import React, {forwardRef, useContext} from 'react';

import mergeRefs from 'merge-refs';

import {FloatingMenuItem} from '@pb/kit-component-menu';

import {DropdownMenuContext} from './dropdown-menu-context';
import {NestedMenu} from './nested-menu';

import type {FloatingMenuItemProps} from '@pb/kit-component-menu';
import type {MouseEventHandler, ReactNode} from 'react';


type FilteredMenuProps = Omit<FloatingMenuItemProps, 'children'>;

export interface DropdownMenuItemProps extends FilteredMenuProps {
    /**
     * Which side the nested sub-menu panel opens toward when this item has
     * `children`. `"right"` (default) tries `right-start` first and falls back
     * to left-side placements; `"left"` does the opposite.
     */
    nestedListDirection?: 'right' | 'left',

    /**
     * `<DropdownMenuItem>` nodes that form a nested sub-menu. When present and
     * the item is not `disabled`, the item is promoted to a `<NestedMenu>` trigger
     * that opens on pointer enter. Use this to build multi-level menu trees.
     */
    children?: ReactNode,

    /**
     * Render prop for the item's visible content. Receives the same `props` as
     * `FloatingMenuItem` children, but bypasses the default caption `<span>`
     * wrapper. Use when you need multi-line text, icons, or a fully custom layout
     * inside the item row while keeping all keyboard/focus interaction intact.
     */
    renderCaption?: FloatingMenuItemProps['children'],
}

/**
 * Interactive row inside a `<DropdownMenu>` panel.
 *
 * Wraps `<FloatingMenuItem>` with two extra behaviors:
 * - When `children` are provided (and the item is not `disabled`), it promotes
 *   itself to a `<NestedMenu>` trigger that opens a side panel on hover.
 * - When no children, it calls `onItemSelect` from `DropdownMenuContext` on
 *   click so the root `DropdownMenu` can dismiss all levels (when
 *   `hideOnItemClick` is set).
 *
 * Supply `renderCaption` for custom row content; use `nestedListDirection` to
 * control which side the sub-menu panel opens toward.
 */
export const DropdownMenuItem = forwardRef<HTMLElement, DropdownMenuItemProps>((props, ref) => {
    const {
        nestedListDirection,
        children,
        disabled,
        renderCaption,
        ...restProps
    } = props;

    const {onItemSelect} = useContext(DropdownMenuContext);

    if (children && !disabled) {
        return (
            <NestedMenu
                anchor={({anchorRef, getAnchorProps}) => (
                    <FloatingMenuItem
                        {...getAnchorProps({
                            ...restProps,
                            onClick: restProps.onClick as MouseEventHandler,
                        })}
                        ref={mergeRefs<HTMLElement>(ref, anchorRef)}>
                        {renderCaption}
                    </FloatingMenuItem>
                )}
                nestedListDirection={nestedListDirection}>
                {children}
            </NestedMenu>
        );
    }

    return (
        <FloatingMenuItem
            disabled={disabled}
            {...restProps}
            onClick={e => {
                restProps.onClick?.(e);
                onItemSelect?.();
            }}
            ref={ref}>
            {renderCaption}
        </FloatingMenuItem>
    );
});

DropdownMenuItem.displayName = 'DropdownMenuItem';
